import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Role, UserAdmin } from '../../shared/models/user.model';

declare const moment: (input: string) => { format: (pattern: string) => string };

export function createBalanceColumn(formatCurrency: (user: UserAdmin) => string): ColDef {
  return {
    headerName: 'Balance (€)',
    field: 'balance',
    maxWidth: 120,
    minWidth: 120,
    cellRenderer: (params: ICellRendererParams) =>
      `<div class="one-line d-flex justify-content-center align-items-center gap-0_8">${formatCurrency(params.data)}</div>`,
  };
}

export function createLastActiveColumn(formatLastLogin: (params: ICellRendererParams) => string): ColDef {
  return {
    headerName: 'Last active',
    field: 'last_active',
    maxWidth: 90,
    minWidth: 90,
    sortable: true,
    comparator: () => 0,
    initialSort: null,
    cellRenderer: (params: ICellRendererParams) => `<div class="two-lines"> ${formatLastLogin(params)}</div>`,
  };
}

export const usersAccountColumns: Record<string, ColDef> = {
  lastTopUp: {
    headerName: 'Last top up',
    field: 'lastTopUp',
    initialHide: true,
    minWidth: 105,
    maxWidth: 120,
    sortable: true,
    comparator: () => 0,
    sortingOrder: ['desc', 'asc', null],
    cellRenderer: (params: ICellRendererParams<UserAdmin>) => {
      if (params.data?.role !== Role.MAIN || !params.data.lastTopUp) {
        return '<div class="one-line">-</div>';
      }
      const date = moment(params.data.lastTopUp);
      return `<div class="two-lines">${date.format('HH:mm:ss')}<br>${date.format('DD/MM/YY')}</div>`;
    },
  },
  externalUserId: {
    headerName: 'External User ID',
    field: 'externalUserId',
    initialHide: true,
    flex: 0.5,
    cellRenderer: (params: ICellRendererParams) => {
      const title = params.value;
      return `<div class="one-line" title="${title}">${params.value}</div>`;
    },
  },
  company: {
    headerName: 'Company',
    field: 'companyName',
    flex: 0.5,
    cellRenderer: (params: ICellRendererParams) => {
      const title = params.value;
      return `<div class="one-line" title="${title}">${params.value}</div>`;
    },
  },
  department: {
    headerName: 'Department',
    field: 'departmentName',
    flex: 0.5,
    initialHide: true,
    cellRenderer: (params: ICellRendererParams) => {
      const department = params.data.departmentName ? `${params.data.departmentName}` : '';
      return `<div class="one-line">${department}</div>`;
    },
  },
  paymentType: {
    headerName: 'Payment Terms',
    field: 'paymentType',
    flex: 0.3,
    hide: true,
  },
  rankingIndex: {
    headerName: 'Ranking index',
    field: 'rankingIndex',
    initialHide: true,
    width: 120,
    sortable: true,
    comparator: () => 0,
    valueGetter: params => getUserRankingIndex(params.data),
    valueFormatter: params => (params.value == null ? '–' : String(params.value)),
  },
};

export function getUserRankingIndex(user: UserAdmin | undefined): number | null {
  return user?.role === Role.MAIN && Number.isInteger(user.rankingIndex) && user.rankingIndex >= 0 && user.rankingIndex <= 10
    ? user.rankingIndex
    : null;
}
